export interface TokenValidation {
  exists: boolean;
  isValid: boolean;
  isExpired: boolean;
  expiresAt?: Date;
  timeRemaining?: number;
}

interface JWTPayload {
  exp?: number;
  iat?: number;
  [key: string]: any;
}

interface ParsedTokenData {
  secret?: string;
  credential?: string;
  accessToken?: string;
  idToken?: string;
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accesstoken',
  ID_TOKEN: 'idtoken',
  AUTH_RESULT: import.meta.env.VITE_APP_AZURE_AUTH_RESULT || 'portal-auth-result',
} as const;

const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const MINUTES_IN_HOUR = 60;
const HOURS_IN_DAY = 24;

const createInvalidTokenValidation = (exists: boolean = false): TokenValidation => ({
  exists,
  isValid: false,
  isExpired: true,
});

const calculateTimeRemaining = (expirationTime: number): number => {
  const remaining = expirationTime - Date.now();
  return remaining > 0 ? remaining : 0;
};

export function validateToken(): TokenValidation {
  try {
    const accessToken = findAccessToken();
    
    if (!accessToken) {
      return createInvalidTokenValidation();
    }

    const tokenData = decodeJWT(accessToken);
    
    if (!tokenData?.exp) {
      return createInvalidTokenValidation(true);
    }

    const expirationTime = tokenData.exp * MILLISECONDS_IN_SECOND;
    const isExpired = Date.now() >= expirationTime;

    return {
      exists: true,
      isValid: !isExpired,
      isExpired,
      expiresAt: new Date(expirationTime),
      timeRemaining: calculateTimeRemaining(expirationTime),
    };
  } catch (error) {
    return createInvalidTokenValidation();
  }
}

function findAccessToken(): string | null {
  try {
    const tokenFromMSAL = findTokenInMSALStorage();
    if (tokenFromMSAL) return tokenFromMSAL;

    const tokenFromAuthResult = findTokenInAuthResult();
    if (tokenFromAuthResult) return tokenFromAuthResult;

    return null;
  } catch (error) {
    return null;
  }
}

function findTokenInMSALStorage(): string | null {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    const isMSALKey = key.includes(STORAGE_KEYS.ACCESS_TOKEN) || 
                      key.includes(STORAGE_KEYS.ID_TOKEN);
    
    if (isMSALKey) {
      const token = extractTokenFromValue(localStorage.getItem(key));
      if (token) return token;
    }
  }
  return null;
}

function findTokenInAuthResult(): string | null {
  const authResult = localStorage.getItem(STORAGE_KEYS.AUTH_RESULT);
  if (!authResult) return null;

  try {
    const parsed: ParsedTokenData = JSON.parse(authResult);
    return parsed.accessToken || parsed.idToken || null;
  } catch {
    return null;
  }
}

function extractTokenFromValue(value: string | null): string | null {
  if (!value) return null;

  try {
    const parsed: ParsedTokenData = JSON.parse(value);
    return parsed.secret || parsed.credential || null;
  } catch {
    return value.includes('.') ? value : null;
  }
}

function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

export function formatTimeRemaining(milliseconds: number): string {
  if (milliseconds <= 0) return 'Expirado';
  
  const seconds = Math.floor(milliseconds / MILLISECONDS_IN_SECOND);
  const minutes = Math.floor(seconds / SECONDS_IN_MINUTE);
  const hours = Math.floor(minutes / MINUTES_IN_HOUR);
  const days = Math.floor(hours / HOURS_IN_DAY);

  if (days > 0) return `${days}d ${hours % HOURS_IN_DAY}h`;
  if (hours > 0) return `${hours}h ${minutes % MINUTES_IN_HOUR}m`;
  if (minutes > 0) return `${minutes}m ${seconds % SECONDS_IN_MINUTE}s`;
  return `${seconds}s`;
}
